import { inject } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { SHARED_API_CONFIG } from '../config/api.config';
import { FeedbackService } from './feedback.service';
import { HttpService } from './http.service';

export interface PaginatedResponse<T> {
  count: number;
  results: T[];
}

export abstract class BaseCrudService<T> {
  protected feedback = inject(FeedbackService);
  protected http = inject(HttpService);
  private _apiConfig = inject(SHARED_API_CONFIG);

  protected overrideCreateEndpoint = '';
  protected overrideUpdateEndpoint = '';
  protected overrideDeleteEndpoint = '';
  protected overrideGetByIdEndpoint = '';
  protected overrideGetAllEndpoint = '';
  protected overrideGetPagedEndpoint = '';
  protected overrideGetPagedFilterEndpoint = '';

  protected createFeedback = '';
  protected updateFeedback = '';
  protected deleteFeedback = '';

  constructor(
    protected endpoint: string,
    protected suffix: string = '',
    protected apiBaseUrl: string = ''
  ) {
    this.apiBaseUrl = apiBaseUrl || this._apiConfig.apiBaseUrl;
  }

  // =========================
  // Helpers
  // =========================

  protected _getEndpoint(override: string, id?: number | string): string {
    const endpointPath = override || this.endpoint;
    const withSuffix = this.suffix ? `/${this.suffix}` : '';
    return id ? `${endpointPath}/${id}${withSuffix}` : `${endpointPath}${withSuffix}`;
  }

  // =========================
  // CRUD
  // =========================

  getAll(): Observable<T[]> {
    const endpoint = this._getEndpoint(this.overrideGetAllEndpoint, undefined);
    return this.http.get<T[]>(endpoint);
  }

  getPaged(block: number, size: number): Observable<PaginatedResponse<T>> {
    const params = new URLSearchParams({
      block: block.toString(),
      size: size.toString(),
    });

    const endpoint = `${this._getEndpoint(this.overrideGetPagedEndpoint, undefined)}?${params.toString()}`;

    return this.http.get<PaginatedResponse<T>>(endpoint);
  }

  getPagedFilter(
    block: number,
    size: number,
    filters: Record<string, string | number | boolean | (string | number | boolean)[]> = {}
  ): Observable<PaginatedResponse<T>> {
    const params = new URLSearchParams({
      block: block.toString(),
      size: size.toString(),
    });

    Object.entries(filters).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') return;

      if (Array.isArray(value)) {
        value.forEach((v) => params.append(key, String(v)));
      } else {
        params.append(key, String(value));
      }
    });

    const endpoint = `${this._getEndpoint(this.overrideGetPagedFilterEndpoint, undefined)}?${params.toString()}`;

    return this.http.get<PaginatedResponse<T>>(endpoint);
  }

  getById(id: number | string): Observable<T> {
    const endpoint = this._getEndpoint(this.overrideGetByIdEndpoint, id);
    return this.http.get<T>(endpoint);
  }

  create(item: Partial<T> | FormData): Observable<T> {
    const endpoint = this._getEndpoint(this.overrideCreateEndpoint);
    return this.http.post<T>(endpoint, item).pipe(
      tap(() => {
        if (this.createFeedback) {
          this.feedback.showSuccess({ message: this.createFeedback });
        }
      })
    );
  }

  update(item: Partial<T> | FormData): Observable<T> {
    const endpoint = this._getEndpoint(this.overrideUpdateEndpoint, undefined);
    return this.http.put<T>(endpoint, item).pipe(
      tap(() => {
        if (this.updateFeedback) {
          this.feedback.showSuccess({ message: this.updateFeedback });
        }
      })
    );
  }

  delete(item: Partial<T> | FormData): Observable<void> {
    const endpoint = this._getEndpoint(this.overrideDeleteEndpoint, undefined);
    return this.http.delete<void>(endpoint, item).pipe(
      tap(() => {
        if (this.deleteFeedback) {
          this.feedback.showSuccess({ message: this.deleteFeedback });
        }
      })
    );
  }
}
